import { buildReceiverOptions, initReceiver } from './receiver';
import {
  installLoadInterceptor,
  getPreferredAudioLanguage,
  clearPreferredAudioLanguage,
} from './interceptors/load';
import { installEditAudioTracksInterceptor } from './interceptors/editAudioTracks';
import { initLogger, log, logStartupBanner } from './logging/logger';
import { summarizeAudioTracks } from './tracks/audioTracks';

initLogger();

const { context, playerManager } = initReceiver();

installLoadInterceptor(playerManager);
installEditAudioTracksInterceptor(playerManager);

function describeTracks(): {
  total: number;
  audio: ReturnType<typeof summarizeAudioTracks>;
  video: framework.messages.Track[];
  text: framework.messages.Track[];
} {
  const tracks = playerManager.getMediaInformation()?.tracks ?? [];
  const audio = summarizeAudioTracks(
    tracks.map((t: framework.messages.Track) => ({
      trackId: t.trackId,
      type: String(t.type),
      language: t.language,
      name: t.name,
    })),
  );
  return {
    total: tracks.length,
    audio,
    video: tracks.filter((t) => t.type === cast.framework.messages.TrackType.VIDEO),
    text: tracks.filter((t) => t.type === cast.framework.messages.TrackType.TEXT),
  };
}

function activeTrackIds(): number[] {
  // PlayerManager exposes getMediaStatus().activeTrackIds in some SDK builds
  // and getActiveTracks via different APIs. Walk both safely.
  const ms = (playerManager as unknown as {
    getMediaStatus?: () => { activeTrackIds?: number[] };
  }).getMediaStatus?.();
  return ms?.activeTrackIds ?? [];
}

// --- Lifecycle events --------------------------------------------------------

playerManager.addEventListener(cast.framework.events.EventType.PLAYER_LOADING, () => {
  log.info('EVENT', 'PLAYER_LOADING');
});

playerManager.addEventListener(cast.framework.events.EventType.PLAYER_LOAD_COMPLETE, () => {
  const { total, audio, video, text } = describeTracks();
  log.info('READY', 'PLAYER_LOAD_COMPLETE', {
    totalTracks: total,
    audioCount: audio.length,
    videoCount: video.length,
    textCount: text.length,
    audioLanguages: audio.map((t) => t.normalizedLang || '(empty)').join(','),
  });
  for (const t of audio) {
    log.info('TRACK', 'audio rendition', {
      id: t.id,
      language: t.normalizedLang || '(empty)',
      name: t.name,
    });
  }
  // Earliest reliable "media is ready" signal — hide the splash. The
  // LOADED_METADATA / LOADED_DATA / PLAYING handlers below are belt-and-
  // suspenders for paths where this might fire late on Cast 3rd gen.
  setMediaActive(true);

  const preferred = getPreferredAudioLanguage();
  if (preferred) {
    const request = new cast.framework.messages.EditAudioTracksRequestData();
    request.language = preferred;
    playerManager.sendLocalMediaRequest(request);
    clearPreferredAudioLanguage();
    log.info('TRACKS', 'applied preferredAudioLanguage from LOAD customData', {
      preferred,
    });
  }
});

function setMediaActive(active: boolean): void {
  document.body.classList.toggle('media-active', active);
}

// Hide the splash on any signal that media has started — whichever fires
// first wins. LOADED_DATA is the "right" signal but doesn't always fire on
// Cast 3rd gen with this Shaka build. PLAYER_LOAD_COMPLETE and PLAYING are
// reliable fallbacks. Idempotent because setMediaActive just toggles a class.
playerManager.addEventListener(cast.framework.events.EventType.LOADED_METADATA, () => {
  log.info('EVENT', 'LOADED_METADATA');
  setMediaActive(true);
});

playerManager.addEventListener(cast.framework.events.EventType.LOADED_DATA, () => {
  log.info('EVENT', 'LOADED_DATA (first frame ready)');
  setMediaActive(true);
});

playerManager.addEventListener(cast.framework.events.EventType.PLAYING, () => {
  log.info('EVENT', 'PLAYING');
  setMediaActive(true);
});

playerManager.addEventListener(cast.framework.events.EventType.PAUSE, () => {
  log.info('EVENT', 'PAUSE');
});

playerManager.addEventListener(
  cast.framework.events.EventType.BUFFERING,
  (e: framework.events.BufferingEvent) => {
    log.info('EVENT', 'BUFFERING', { isBuffering: e.isBuffering });
  },
);

playerManager.addEventListener(
  cast.framework.events.EventType.BITRATE_CHANGED,
  (e: framework.events.BitrateChangedEvent) => {
    log.info('EVENT', 'BITRATE_CHANGED', { totalBitrate: e.totalBitrate });
  },
);

playerManager.addEventListener(cast.framework.events.EventType.MEDIA_FINISHED, () => {
  log.info('EVENT', 'MEDIA_FINISHED');
  setMediaActive(false);
});

playerManager.addEventListener(
  cast.framework.events.EventType.MEDIA_INFORMATION_CHANGED,
  () => {
    const { total, audio } = describeTracks();
    log.info('EVENT', 'MEDIA_INFORMATION_CHANGED', {
      totalTracks: total,
      audioLanguages: audio.map((t) => t.normalizedLang || '(empty)').join(','),
    });
  },
);

playerManager.addEventListener(cast.framework.events.EventType.TRACKS_CHANGED, () => {
  const ids = activeTrackIds();
  const { audio } = describeTracks();
  const activeAudio = audio.find((t) => ids.includes(t.id));
  log.info('EVENT', 'TRACKS_CHANGED', {
    activeTrackIds: ids.join(',') || '(none)',
    activeAudioId: activeAudio?.id ?? '(none)',
    activeAudioLanguage: activeAudio?.normalizedLang || '(none)',
  });
});

// --- Errors ------------------------------------------------------------------

playerManager.addEventListener(
  cast.framework.events.EventType.ERROR,
  (e: framework.events.ErrorEvent) => {
    const inner = (e.error ?? {}) as {
      category?: unknown;
      code?: unknown;
      severity?: unknown;
      data?: unknown;
      message?: unknown;
    };
    log.error('ERROR', 'PLAYER_ERROR', {
      type: e.type,
      detailedErrorCode: e.detailedErrorCode ?? '',
      reason: e.reason ?? '',
      severity: e.severity ?? '',
      shakaCategory: inner.category ?? '',
      shakaCode: inner.code ?? '',
      shakaSeverity: inner.severity ?? '',
      shakaData: inner.data ? JSON.stringify(inner.data) : '',
      message: inner.message ?? '',
    });
  },
);

// --- Sender connect / disconnect --------------------------------------------

context.addEventListener(cast.framework.system.EventType.SENDER_CONNECTED, (event) => {
  const e = event as framework.system.SenderConnectedEvent;
  log.info('SENDER', 'connected', { senderId: e.senderId, userAgent: e.userAgent });
});

context.addEventListener(cast.framework.system.EventType.SENDER_DISCONNECTED, (event) => {
  const e = event as framework.system.SenderDisconnectedEvent;
  log.info('SENDER', 'disconnected', { senderId: e.senderId, reason: e.reason ?? '' });
  setMediaActive(false);
});

// --- Boot --------------------------------------------------------------------

const { options: receiverOptions, profile, supports4K } = buildReceiverOptions(context);
context.start(receiverOptions);

const deviceCapabilities = (
  context as unknown as { getDeviceCapabilities?: () => Record<string, unknown> }
).getDeviceCapabilities?.();

logStartupBanner({
  cafSdkVersion: cast.framework.VERSION ?? 'unknown',
  profile,
  supports4K,
  display: deviceCapabilities?.['display_supported'] ?? '',
  isGroup: deviceCapabilities?.['is_group'] ?? '',
});
